/* eslint-disable no-barrel-files/no-barrel-files -- existing url helpers barrel */
import { linkOptions } from "@tanstack/react-router";

import { appendQueryString } from "./appendQueryString";
import { absolutePath } from "./internalApi/absolutePath";
import { toQueryString } from "./toQueryString";

export * from "./internalApi";
export * from "./toQueryString";
export * from "./publicLiveStreams";
export * from "./getRouteWithPrevLocation";
export * from "./workflowsSettingsPaths";
export * from "./publicNotificationPreferences";
export * from "./settingsNavPathV3";
export * from "./mediaTranscriptPaths";
export * from "./memberProfileModalPaths";
export * from "./adminMemberEditPaths";
export * from "./settingsPath";
export * from "./notificationsApi";
export * from "./authPathsWithRedirect";
export * from "./coursePaths";
export * from "./userProfilePaths";
export * from "./liveStreamPaths";
export * from "./eventsPaths";
export * from "./staticPages";
export * from "./posts";
export * from "./userPaths";
export * from "./communityMembersPath";
export * from "./memberSettingsPaths";
export * from "./spaceGroupPaths";
export * from "./spacePaths";
export * from "./activeStorageBlobUrl";

type Params = Record<string, unknown>;

export const signOutPath = () => "/users/sign_out";

export const notificationsPath = () => "/internal_api/notifications";
export const newNotificationsCountPath = () =>
  "/internal_api/notifications/new_notifications_count";

export const chatRoomPath = ({
  uuid,
  messageHashId,
}: {
  uuid: string;
  messageHashId?: string;
}) => {
  let chatRoomUrl: string = `/messages/${uuid}`;
  if (messageHashId) {
    chatRoomUrl = `${chatRoomUrl}?message_id=${messageHashId}#message_${messageHashId}`;
  }
  return chatRoomUrl;
};

export const chatRoomLinkOptions = ({
  uuid,
  messageHashId,
}: {
  uuid: string;
  messageHashId?: string;
}) =>
  linkOptions({
    to: "/messages/$uuid",
    params: { uuid },
    hash: messageHashId ? `message_${messageHashId}` : undefined,
    search: messageHashId ? { message_id: messageHashId } : undefined,
  });

export const chatBotPath = ({ id }: { id: string }) => `/messages/bot/${id}`;
export const chatInboxPath = ({ id }: { id: string }) =>
  `/settings/ai-inbox/conversations/${id}`;
export const chatRoomsPath = () => "/messages" as const;
export const newChatRoomPath = () => "/messages/new" as const;
export const chatThreadsPath = () => "/threads" as const;
export const bookmarksPath = () => "/bookmarks" as const;

export const communitySwitcherPath = () => "/community_switchers";
export const communitySwitcherReorderPath = () =>
  "/community_switchers/reorder";
export const createNewCommunityPath = () => "/communities/new";
export const directUploadPath = () => absolutePath("direct_uploads");
export const embedsPath = () => "/embeds";

export const actionTextAttachmentsPath = (sgid: string) =>
  `/action_texts/attachments/${sgid}`;

export const updateActionTextAttachmentPropertiesPath = (sgid: string) =>
  `/action_texts/attachments/${sgid}/update_properties`;

export const chatRoomInviteLink = (chat_room_uuid: string) =>
  `${window.location.protocol}//${window.location.host}/messages/${chat_room_uuid}/invite_member`;

export const chatSpacePath = ({
  spaceSlug,
  messageHashId,
}: {
  spaceSlug: string;
  messageHashId?: string;
}) => {
  let chatSpaceUrl: string = `/c/${spaceSlug}`;
  if (messageHashId) {
    chatSpaceUrl = `${chatSpaceUrl}?message_id=${messageHashId}#message_${messageHashId}`;
  }
  return chatSpaceUrl;
};

export const chatSpaceLinkOptions = ({
  spaceSlug,
  messageHashId,
}: {
  spaceSlug: string;
  messageHashId?: string;
}) =>
  linkOptions({
    to: "/c/$spaceSlug",
    params: { spaceSlug },
    hash: messageHashId ? `message_${messageHashId}` : undefined,
    search: messageHashId ? { message_id: messageHashId } : undefined,
  });

export const viewSpacePath = ({
  spaceSlug,
  params = {},
}: {
  spaceSlug: string;
  params?: Params;
}) => {
  const query = toQueryString(params);
  return query ? `/c/${spaceSlug}?${query}` : `/c/${spaceSlug}`;
};

export const isSettingsArea = () =>
  window.location.pathname.startsWith("/settings");

export const joinSpacePath = ({ params = {} }: { params?: Params } = {}) =>
  `/space_members/new?${toQueryString(params)}`;
export const homePagePostsPath = () => "/home/home_page/posts";
export const homePageUpcomingEventsPath = () => "/home/home_page/events";
export const homePageSpacesPath = () => "/home/home_page/spaces";
export const homePageBannerPath = () => "/home/home_page/banners";
export const homePageMemberActivityPath = () =>
  "/home/home_page/member_activities";

export const removeOpengraphImagePath = (meta_tag_id: string) =>
  `/opengraph_images/${meta_tag_id}`;
export const resetCommunityWelcomeBannerPreferencePath = () =>
  "/settings/landing_page/welcome_banner_preference";

export const newSessionPath = ({ params }: { params?: Params } = {}) =>
  `/users/sign_in?${toQueryString(params)}`;

export const spaceUpdatePath = (id: string, params: Params = {}) =>
  `/internal_api/spaces/${id}?${toQueryString(params)}`;

export const spacePath = (id: string) => `/internal_api/spaces/${id}`;

export const twoFaPath = () => "/two_fa";

export const mentions = {
  index: ({ params }: { params?: Params } = {}) =>
    `/users/mentions.json?${toQueryString(params)}`,
};

export const blobPath = ({
  filename,
  signed_id,
}: {
  filename?: string;
  signed_id: string;
}) =>
  // filename does not matter
  `/rails/active_storage/blobs/redirect/${signed_id}/${filename || "download"}`;

export const userLikes = {
  create: ({ params }: { params?: Params } = {}) =>
    `/user_likes?${toQueryString(params)}`,
  destroy: ({ params }: { params?: Params } = {}) =>
    `/user_likes?${toQueryString(params)}`,
};

export const spaces = {
  show: ({ spaceSlug, params = {} }: { spaceSlug: string; params?: Params }) =>
    appendQueryString(`/c/${spaceSlug}`, params),
  update: ({ spaceSlug }: { spaceSlug: string }) => `/c/${spaceSlug}`,
};

export const planSettingsPath = () => "/settings/plans" as const;

export const communityInboxPath = () => "/settings/ai-inbox" as const;

export const notificationsFullPagePath = () => "/notifications" as const;

export const connectionsFullPagePath = () => "/connections" as const;
